import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { PlusCircle, Info } from "lucide-react";
import { revisedQuoteFinance } from "@/lib/revisedQuoteFinance";

interface Props {
  leadId: string;
  acceptedQuotationId: string | null;
  currency: string;
}

/** Shows a top-up amount when the accepted (revised) quote exceeds what was invoiced. */
export const TopUpInvoicePanel = ({ leadId, acceptedQuotationId, currency }: Props) => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["topup-check", leadId, acceptedQuotationId],
    enabled: !!acceptedQuotationId,
    queryFn: async () => {
      const [{ data: q }, { data: inv }] = await Promise.all([
        supabase.from("quotations").select("quote_number,total_amount").eq("id", acceptedQuotationId!).maybeSingle(),
        supabase.from("invoices").select("amount,amount_received,status,installment_number").eq("lead_id", leadId),
      ]);
      return { quote: q, invoices: inv || [] };
    },
  });

  const fin = data?.quote && data.invoices.length
    ? revisedQuoteFinance(Number(data.quote.total_amount || 0), data.invoices as any)
    : null;

  const create = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !fin) throw new Error("Not ready");
      const next = Math.max(0, ...data!.invoices.map((i: any) => i.installment_number || 0)) + 1;
      const pct = Math.round((fin.topUpDue / Number(data!.quote!.total_amount)) * 10000) / 100;
      const { error } = await supabase.from("invoices").insert({
        lead_id: leadId,
        user_id: user.id,
        installment_number: next,
        percentage: pct,
        amount: fin.topUpDue,
        due_date: new Date().toISOString().slice(0, 10),
        status: "pending",
        shortfall_notes: `Top-up for revised quote ${data!.quote!.quote_number}`,
      } as any);
      if (error) throw error;
      await supabase.from("activity_log").insert({
        lead_id: leadId,
        user_id: user.id,
        action: "Top-up Invoice Created",
        details: `Top-up of ${currency} ${fin.topUpDue.toLocaleString()} for revised quote ${data!.quote!.quote_number}`,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices", leadId] });
      qc.invalidateQueries({ queryKey: ["topup-check", leadId] });
      qc.invalidateQueries({ queryKey: ["unified-activity-feed", leadId] });
      toast.success("Top-up invoice created — issue it below to send for payment");
    },
    onError: (e: any) => toast.error(e.message || "Could not create top-up invoice"),
  });

  if (!fin || (!fin.topUpDue && !fin.overInvoiced)) return null;

  return (
    <Card className="p-4 border-primary/40 bg-primary/5 flex flex-wrap items-center gap-3 justify-between">
      <div className="flex items-start gap-2 text-sm min-w-0">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
        {fin.topUpDue ? (
          <span>
            Revised quote {data!.quote!.quote_number} is <strong>{currency} {fin.topUpDue.toLocaleString()}</strong> more than already invoiced. Top-up due.
          </span>
        ) : (
          <span>
            Revised quote {data!.quote!.quote_number} is {currency} {fin.overInvoiced.toLocaleString()} less than invoiced — check with finance about a credit or refund.
          </span>
        )}
      </div>
      {fin.topUpDue > 0 && (
        <Button size="sm" className="gap-2" onClick={() => create.mutate()} disabled={create.isPending}>
          <PlusCircle className="h-4 w-4" /> Raise top-up invoice
        </Button>
      )}
    </Card>
  );
};
